import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faPlus,
  faPlay,
  faPause,
  faTrash,
  faEdit,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faChartLine,
  faHistory,
  faProjectDiagram,
  faListAlt,
  faRocket,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faExclamationTriangle,
  faBan
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import schedulerService from '../services/schedulerService';
import projectService from '../services/projectService';
import playbookService from '../services/playbookService';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';

const AssessmentScheduler = () => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [schedules, setSchedules] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list'); // 'list' or 'calendar'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project: '',
    playbook: '',
    assessmentType: 'full',
    schedule: {
      type: 'once',
      runAt: '',
      timeOfDay: '09:00',
      daysOfWeek: [1], // Monday default
      dayOfMonth: 1
    },
    notifications: {
      onStart: { enabled: true, channels: ['in_app'] },
      onComplete: { enabled: true, channels: ['in_app'] },
      onFailure: { enabled: true, channels: ['in_app', 'email'] }
    },
    tags: []
  });

  useEffect(() => {
    fetchData();
  }, [filterProject, filterStatus]);

  useEffect(() => {
    if (activeView === 'calendar') {
      fetchCalendarEvents();
    }
  }, [activeView, currentMonth, filterProject]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [schedulesRes, projectsRes, playbooksRes, statsRes] = await Promise.all([
        schedulerService.getSchedules({ project: filterProject, status: filterStatus }),
        projectService.getProjects(),
        playbookService.getPlaybooks(),
        schedulerService.getStats()
      ]);

      setSchedules(schedulesRes.schedules || []);
      setProjects(projectsRes || []);
      setPlaybooks(playbooksRes || []);
      setStats(statsRes || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const events = await schedulerService.getCalendarEvents(
        startDate.toISOString(),
        endDate.toISOString(),
        filterProject || null
      );
      setCalendarEvents(events || []);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    try {
      await schedulerService.createSchedule(formData);
      toast.success('Schedule created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error(error.response?.data?.message || 'Failed to create schedule');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await schedulerService.updateStatus(id, status);
      toast.success(`Schedule ${status}`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update schedule status');
    }
  };

  const handleRunNow = async (id) => {
    try {
      const result = await schedulerService.runNow(id);
      toast.success(`Assessment triggered! Created ${result.actionsCreated} actions.`);
      fetchData();
    } catch (error) {
      console.error('Error running schedule:', error);
      toast.error('Failed to trigger assessment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this scheduled assessment?')) {
      return;
    }

    try {
      await schedulerService.deleteSchedule(id);
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      project: '',
      playbook: '',
      assessmentType: 'full',
      schedule: {
        type: 'once',
        runAt: '',
        timeOfDay: '09:00',
        daysOfWeek: [1],
        dayOfMonth: 1
      },
      notifications: {
        onStart: { enabled: true, channels: ['in_app'] },
        onComplete: { enabled: true, channels: ['in_app'] },
        onFailure: { enabled: true, channels: ['in_app', 'email'] }
      },
      tags: []
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'green', icon: faCheckCircle, label: 'Active' },
      paused: { color: 'yellow', icon: faPause, label: 'Paused' },
      completed: { color: 'blue', icon: faCheckCircle, label: 'Completed' },
      cancelled: { color: 'red', icon: faBan, label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full bg-${config.color}-100 text-${config.color}-800 dark:bg-${config.color}-900/30 dark:text-${config.color}-400`}>
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const getScheduleDescription = (schedule) => {
    switch (schedule.type) {
      case 'once':
        return `Once on ${formatDate(schedule.runAt)}`;
      case 'daily':
        return `Daily at ${schedule.timeOfDay || '00:00'}`;
      case 'weekly':
        const days = (schedule.daysOfWeek || [])
          .map(d => schedulerService.DAYS_OF_WEEK.find(day => day.value === d)?.label)
          .join(', ');
        return `Weekly on ${days} at ${schedule.timeOfDay || '00:00'}`;
      case 'monthly':
        return `Monthly on day ${schedule.dayOfMonth} at ${schedule.timeOfDay || '00:00'}`;
      default:
        return schedule.type;
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.getDate() === day &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('scheduler.title') || 'Assessment Scheduler'}
        subtitle={t('scheduler.subtitle') || 'Schedule and automate security assessments'}
        icon={<FontAwesomeIcon icon={faCalendarAlt} />}
      >
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          {t('scheduler.newSchedule') || 'New Schedule'}
        </button>
      </PageHeader>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('scheduler.totalSchedules') || 'Total Schedules'}
              </p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {stats.totalSchedules || 0}
              </p>
            </div>
            <FontAwesomeIcon icon={faCalendarAlt} className="text-2xl text-blue-500" />
          </div>
        </div>

        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('scheduler.totalRuns') || 'Total Runs'}
              </p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {stats.totalRuns || 0}
              </p>
            </div>
            <FontAwesomeIcon icon={faHistory} className="text-2xl text-purple-500" />
          </div>
        </div>

        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('scheduler.successfulRuns') || 'Successful'}
              </p>
              <p className="text-2xl font-bold text-green-500">{stats.successfulRuns || 0}</p>
            </div>
            <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-green-500" />
          </div>
        </div>

        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('scheduler.avgDetectionRate') || 'Avg Detection'}
              </p>
              <p className="text-2xl font-bold text-blue-500">
                {(stats.avgDetectionRate || 0).toFixed(1)}%
              </p>
            </div>
            <FontAwesomeIcon icon={faChartLine} className="text-2xl text-blue-500" />
          </div>
        </div>

        <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('scheduler.upcomingThisWeek') || 'This Week'}
              </p>
              <p className="text-2xl font-bold text-orange-500">{stats.upcomingThisWeek || 0}</p>
            </div>
            <FontAwesomeIcon icon={faClock} className="text-2xl text-orange-500" />
          </div>
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4">
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className={`px-3 py-2 rounded-lg border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              <option value="">{t('scheduler.allProjects') || 'All Projects'}</option>
              {projects.map(project => (
                <option key={project._id} value={project._id}>{project.name}</option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
              }`}
            >
              <option value="">{t('scheduler.allStatuses') || 'All Statuses'}</option>
              <option value="active">{t('scheduler.active') || 'Active'}</option>
              <option value="paused">{t('scheduler.paused') || 'Paused'}</option>
              <option value="completed">{t('scheduler.completed') || 'Completed'}</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setActiveView('list')}
              className={`px-4 py-2 rounded-lg ${
                activeView === 'list'
                  ? 'bg-blue-600 text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={faListAlt} className="mr-2" />
              {t('scheduler.listView') || 'List'}
            </button>
            <button
              onClick={() => setActiveView('calendar')}
              className={`px-4 py-2 rounded-lg ${
                activeView === 'calendar'
                  ? 'bg-blue-600 text-white'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
              {t('scheduler.calendarView') || 'Calendar'}
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {activeView === 'list' && (
        <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {schedules.length === 0 ? (
            <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <FontAwesomeIcon icon={faCalendarAlt} className="text-5xl mb-4 opacity-50" />
              <p className="text-lg mb-2">{t('scheduler.noSchedules') || 'No scheduled assessments'}</p>
              <p className="text-sm">{t('scheduler.createFirst') || 'Create your first schedule to get started'}</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('common.name') || 'Name'}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('common.project') || 'Project'}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('scheduler.schedule') || 'Schedule'}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('scheduler.nextRun') || 'Next Run'}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('common.status') || 'Status'}
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {t('common.actions') || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {schedules.map((schedule) => (
                  <tr key={schedule._id} className={darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4">
                      <div>
                        <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {schedule.name}
                        </div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {schedule.assessmentType}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/projects/${schedule.project?._id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {schedule.project?.name || 'Unknown'}
                      </Link>
                    </td>
                    <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {getScheduleDescription(schedule.schedule)}
                    </td>
                    <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {schedule.stats?.nextRunAt
                        ? formatDate(schedule.stats.nextRunAt)
                        : 'Not scheduled'}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(schedule.status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleRunNow(schedule._id)}
                          className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                          title="Run Now"
                        >
                          <FontAwesomeIcon icon={faPlay} />
                        </button>
                        {schedule.status === 'active' ? (
                          <button
                            onClick={() => handleUpdateStatus(schedule._id, 'paused')}
                            className="p-2 text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded"
                            title="Pause"
                          >
                            <FontAwesomeIcon icon={faPause} />
                          </button>
                        ) : schedule.status === 'paused' && (
                          <button
                            onClick={() => handleUpdateStatus(schedule._id, 'active')}
                            className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded"
                            title="Resume"
                          >
                            <FontAwesomeIcon icon={faPlay} />
                          </button>
                        )}
                        <button
                          onClick={() => setShowDetailModal(schedule)}
                          className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faHistory} />
                        </button>
                        <button
                          onClick={() => handleDelete(schedule._id)}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Calendar View */}
      {activeView === 'calendar' && (
        <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Calendar Header */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div
                key={day}
                className={`text-center py-2 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {getDaysInMonth(currentMonth).map((day, index) => {
              const events = getEventsForDay(day);
              const isToday = day && new Date().getDate() === day &&
                new Date().getMonth() === currentMonth.getMonth() &&
                new Date().getFullYear() === currentMonth.getFullYear();

              return (
                <div
                  key={index}
                  className={`min-h-[100px] p-2 border rounded ${
                    darkMode ? 'border-gray-700' : 'border-gray-200'
                  } ${day ? '' : 'bg-gray-100 dark:bg-gray-900'} ${
                    isToday ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${
                        isToday ? 'text-blue-600' : darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {day}
                      </div>
                      <div className="space-y-1">
                        {events.slice(0, 3).map((event, i) => (
                          <div
                            key={i}
                            className="text-xs p-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 truncate cursor-pointer hover:bg-blue-200"
                            title={event.title}
                            onClick={() => setShowDetailModal(schedules.find(s => s._id === event.id))}
                          >
                            {event.title}
                          </div>
                        ))}
                        {events.length > 3 && (
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            +{events.length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('scheduler.newSchedule') || 'New Scheduled Assessment'}
              </h2>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleCreateSchedule} className="space-y-4">
              {/* Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('common.name') || 'Name'} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="Weekly Security Assessment"
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('common.description') || 'Description'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
              </div>

              {/* Project */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('common.project') || 'Project'} *
                </label>
                <select
                  required
                  value={formData.project}
                  onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="">{t('scheduler.selectProject') || 'Select Project'}</option>
                  {projects.map(project => (
                    <option key={project._id} value={project._id}>{project.name}</option>
                  ))}
                </select>
              </div>

              {/* Assessment Type */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('scheduler.assessmentType') || 'Assessment Type'}
                </label>
                <select
                  value={formData.assessmentType}
                  onChange={(e) => setFormData({ ...formData, assessmentType: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {schedulerService.ASSESSMENT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Playbook (if assessment type is playbook) */}
              {formData.assessmentType === 'playbook' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('scheduler.playbook') || 'Playbook'}
                  </label>
                  <select
                    value={formData.playbook}
                    onChange={(e) => setFormData({ ...formData, playbook: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="">{t('scheduler.selectPlaybook') || 'Select Playbook'}</option>
                    {playbooks.map(playbook => (
                      <option key={playbook._id} value={playbook._id}>{playbook.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Schedule Type */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('scheduler.scheduleType') || 'Schedule Type'}
                </label>
                <select
                  value={formData.schedule.type}
                  onChange={(e) => setFormData({
                    ...formData,
                    schedule: { ...formData.schedule, type: e.target.value }
                  })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {schedulerService.SCHEDULE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Schedule Details based on type */}
              {formData.schedule.type === 'once' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('scheduler.runAt') || 'Run At'}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.schedule.runAt}
                    onChange={(e) => setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, runAt: e.target.value }
                    })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              )}

              {['daily', 'weekly', 'monthly'].includes(formData.schedule.type) && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('scheduler.timeOfDay') || 'Time of Day'}
                  </label>
                  <input
                    type="time"
                    value={formData.schedule.timeOfDay}
                    onChange={(e) => setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, timeOfDay: e.target.value }
                    })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              )}

              {formData.schedule.type === 'weekly' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('scheduler.daysOfWeek') || 'Days of Week'}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {schedulerService.DAYS_OF_WEEK.map(day => (
                      <label key={day.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.schedule.daysOfWeek.includes(day.value)}
                          onChange={(e) => {
                            const days = e.target.checked
                              ? [...formData.schedule.daysOfWeek, day.value]
                              : formData.schedule.daysOfWeek.filter(d => d !== day.value);
                            setFormData({
                              ...formData,
                              schedule: { ...formData.schedule, daysOfWeek: days }
                            });
                          }}
                          className="mr-2"
                        />
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                          {day.label.slice(0, 3)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.schedule.type === 'monthly' && (
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('scheduler.dayOfMonth') || 'Day of Month'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.schedule.dayOfMonth}
                    onChange={(e) => setFormData({
                      ...formData,
                      schedule: { ...formData.schedule, dayOfMonth: parseInt(e.target.value) }
                    })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className={`px-4 py-2 rounded-lg ${
                    darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  {t('scheduler.createSchedule') || 'Create Schedule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail/History Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                {showDetailModal.name}
              </h2>
              <button
                onClick={() => setShowDetailModal(null)}
                className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</p>
                  <p>{getStatusBadge(showDetailModal.status)}</p>
                </div>
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Runs</p>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {showDetailModal.stats?.totalRuns || 0}
                  </p>
                </div>
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Run</p>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatDate(showDetailModal.stats?.lastRunAt)}
                  </p>
                </div>
                <div>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Next Run</p>
                  <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatDate(showDetailModal.stats?.nextRunAt)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Execution History
                </h3>
                {showDetailModal.executions?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {showDetailModal.executions.slice(0, 10).map((exec, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                            {formatDate(exec.startedAt)}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded ${
                            exec.status === 'completed'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : exec.status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                          }`}>
                            {exec.status}
                          </span>
                        </div>
                        {exec.summary && (
                          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {exec.summary}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No execution history yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentScheduler;
